<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useAgentStore } from '@/stores/agent'
import { icons } from '@/assets/icons'

const agent = useAgentStore()

const AGENT_NAME = 'SenseMate'
const LS_KEY = 'agent-fab-top'
const FAB_SIZE = 96
const MARGIN = 12
const DRAG_THRESHOLD = 4

const top = ref(0)
const dragging = ref(false)
const hovered = ref(false)

let startY = 0
let startTop = 0
let moved = false

function clampTop(y) {
  const max = window.innerHeight - FAB_SIZE - MARGIN
  return Math.min(Math.max(y, MARGIN), Math.max(max, MARGIN))
}

function onPointerDown(e) {
  moved = false
  startY = e.clientY
  startTop = top.value
  dragging.value = true
  e.currentTarget.setPointerCapture?.(e.pointerId)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

function onPointerMove(e) {
  const dy = e.clientY - startY
  if (!moved && Math.abs(dy) > DRAG_THRESHOLD) moved = true
  top.value = clampTop(startTop + dy)
}

function onPointerUp() {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  dragging.value = false
  if (moved) {
    localStorage.setItem(LS_KEY, String(top.value))
  } else {
    agent.toggle()
  }
}

function onResize() {
  top.value = clampTop(top.value)
}

onMounted(() => {
  const saved = Number(localStorage.getItem(LS_KEY))
  const initial = Number.isFinite(saved) && saved > 0
    ? saved
    : window.innerHeight - FAB_SIZE - 24
  top.value = clampTop(initial)
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})
</script>

<template>
  <button
    class="agent-fab"
    :class="{ 'agent-fab--dragging': dragging }"
    :style="{ top: top + 'px' }"
    :aria-label="`打开 ${AGENT_NAME}`"
    @pointerdown="onPointerDown"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <transition name="tip">
      <div v-if="hovered && !dragging" class="fab-tip">{{ AGENT_NAME }}</div>
    </transition>
    <img :src="icons.agent" alt="打开视频问答" class="agent-fab__img" draggable="false" />
  </button>
</template>

<style scoped>
.agent-fab {
  position: fixed;
  right: 24px;
  z-index: 1000;
  border: none;
  background: transparent;
  padding: 0;
  cursor: grab;
  animation: bob 3.2s ease-in-out infinite;
  transition: transform 0.2s ease;
  user-select: none;
  touch-action: none;
}
.agent-fab:hover { transform: translateY(-2px) scale(1.05); }
.agent-fab:focus { outline: none; }

.agent-fab--dragging {
  cursor: grabbing;
  animation: none;
  transition: none;
}
.agent-fab--dragging .agent-fab__img {
  filter: drop-shadow(0 14px 24px rgba(43, 124, 255, 0.45));
}

.agent-fab__img {
  width: 96px;
  height: 96px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 8px 18px rgba(43, 124, 255, 0.28));
  pointer-events: none;
}

/* ===== hover 名字气泡 ===== */
.fab-tip {
  position: absolute;
  right: calc(100% - 8px);
  top: 50%;
  transform: translateY(-50%);
  background: #fff;
  color: #1f2430;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(17, 24, 39, 0.18);
  pointer-events: none;
}
.fab-tip::after {
  content: '';
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: #fff;
  box-shadow: 3px -3px 6px -3px rgba(17, 24, 39, 0.12);
}

/* 气泡进出动画 */
.tip-enter-active, .tip-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.tip-enter-from, .tip-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(6px);
}
.tip-enter-to, .tip-leave-from {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
}
</style>
